import React from 'react';
import { levelForCount } from '../utils/workStatsMetrics';

const LEVEL_CLASS = {
    0: 'bg-zinc-100 dark:bg-zinc-800/80',
    1: 'bg-teal-200/90 dark:bg-teal-900/70',
    2: 'bg-teal-400/90 dark:bg-teal-700/80',
    3: 'bg-teal-600/95 dark:bg-teal-600/90',
    4: 'bg-teal-700 dark:bg-teal-500',
};

const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

const WorkActivityCalendar = ({ calendar, role = 'employee', hint }) => {
    if (!calendar?.weeks?.length) {
        return (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                No activity in this period yet.
            </p>
        );
    }

    const { weeks, monthLabels, maxCount, activeDays, totalEvents } = calendar;
    const cell = 12;
    const gridGap = 4;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {hint ||
                            (role === 'employee'
                                ? 'Days you submitted work or had task activity.'
                                : 'Task creation and review activity on your company.')}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                        {activeDays} active day{activeDays === 1 ? '' : 's'} · {totalEvents}{' '}
                        event{totalEvents === 1 ? '' : 's'} (last ~3 months)
                    </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                    <span>Less</span>
                    {[0, 1, 2, 3, 4].map((level) => (
                        <span
                            key={level}
                            className={`h-3 w-3 rounded-sm ${LEVEL_CLASS[level]}`}
                        />
                    ))}
                    <span>More</span>
                </div>
            </div>

            <div className="overflow-x-auto pb-1">
                <div
                    className="inline-grid"
                    style={{
                        gridTemplateColumns: `32px repeat(${weeks.length}, ${cell}px)`,
                        columnGap: gridGap,
                        rowGap: gridGap,
                    }}
                >
                    <div />
                    {weeks.map((week, wi) => {
                        const month = monthLabels.find((m) => m.weekIndex === wi);
                        return (
                            <span
                                key={`m-${wi}`}
                                className="text-[10px] font-medium text-zinc-500"
                                style={{ height: 16 }}
                            >
                                {month ? month.label : ''}
                            </span>
                        );
                    })}

                    {DAY_LABELS.map((label, row) => (
                        <React.Fragment key={`row-${row}`}>
                            <span className="pr-2 text-right text-[9px] text-zinc-400 leading-[14px]">
                                {label}
                            </span>
                            {weeks.map((week) => {
                                const day = week.days[row];
                                const level = day.inRange
                                    ? levelForCount(day.count, maxCount)
                                    : 0;
                                return (
                                    <span
                                        key={day.dateKey || `${row}-${week.weekStart}`}
                                        title={
                                            day.inRange
                                                ? `${day.label}: ${day.count} event${
                                                      day.count === 1 ? '' : 's'
                                                  }`
                                                : undefined
                                        }
                                        className={`rounded-sm ${
                                            day.inRange
                                                ? LEVEL_CLASS[level]
                                                : 'bg-transparent'
                                        }`}
                                        style={{ width: cell, height: cell }}
                                    />
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WorkActivityCalendar;
